import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMyMatchHistory, useMySquads, useSquadMatchHistory } from '@tark/shared/hooks';
import type { MatchHistoryItem } from '@tark/shared/types';
import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  CircleDot,
  History,
  List,
  Loader2,
  RefreshCw,
  Trophy,
  Users,
} from 'lucide-react';
import { MotivationSummary } from '../components/matches/MotivationSummary';
import { PilotRatingSummary } from '../components/matches/PilotRatingSummary';
import { AppPageFrame } from '../components/AppPageFrame';

type View = 'games' | 'summary' | 'squad';

const views: Array<{ id: View; label: string; icon: typeof List }> = [
  { id: 'games', label: 'My Games', icon: List },
  { id: 'summary', label: 'Summary', icon: BarChart3 },
  { id: 'squad', label: 'Squad Results', icon: Users },
];

export default function PlayPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState<View>('games');
  const { data: squads = [] } = useMySquads();
  const [squadId, setSquadId] = useState<number | null>(null);
  const mine = useMyMatchHistory(view === 'games');
  const squad = useSquadMatchHistory(squadId, view === 'squad');

  useEffect(() => {
    if (squadId == null && squads.length > 0) setSquadId(squads[0].id);
  }, [squadId, squads]);

  useEffect(() => {
    const saved = sessionStorage.getItem('tark:matches:scroll');
    if (!saved) return;
    sessionStorage.removeItem('tark:matches:scroll');
    requestAnimationFrame(() => window.scrollTo({ top: Number(saved), behavior: 'instant' }));
  }, []);

  const query = view === 'squad' ? squad : mine;
  const matches = useMemo(
    () => query.data?.pages.flatMap((page) => page.matches) ?? [],
    [query.data]
  );

  const openMatch = (match: MatchHistoryItem) => {
    sessionStorage.setItem('tark:matches:scroll', String(window.scrollY));
    navigate(`/play/matches/${encodeURIComponent(match.matchId)}`, {
      state: { from: `${location.pathname}${location.search}` },
    });
  };

  return (
    <AppPageFrame className="space-y-6 md:pb-10">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Your play</p>
        <h1 className="mt-2 text-3xl font-bold text-text">Matches</h1>
      </header>

      <div className="grid grid-cols-3 border-b border-border" role="tablist" aria-label="Match views">
        {views.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={view === id}
            onClick={() => setView(id)}
            className={`flex min-h-14 flex-col items-center justify-center gap-1 border-b-2 px-1 text-xs font-semibold transition-colors sm:min-h-12 sm:flex-row sm:gap-2 sm:px-2 sm:text-sm ${
              view === id ? 'border-primary text-primary' : 'border-transparent text-text-muted hover:text-text'
            }`}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="whitespace-nowrap">{label}</span>
          </button>
        ))}
      </div>

      {view === 'summary' ? (
        <div className="space-y-5">
          <PilotRatingSummary />
          <MotivationSummary />
        </div>
      ) : (
        <section aria-live="polite">
          {view === 'squad' ? (
            <label className="mb-5 block max-w-sm">
              <span className="mb-2 block text-sm font-medium text-text">Squad</span>
              <span className="relative block">
                <select
                  aria-label="Choose a squad"
                  value={squadId ?? ''}
                  onChange={(event) => setSquadId(Number(event.target.value))}
                  className="h-11 w-full appearance-none rounded-md border border-border bg-surface px-3 pr-10 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  {squads.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-3 h-5 w-5 text-text-muted" />
              </span>
            </label>
          ) : null}

          {query.isLoading ? <MatchListSkeleton /> : null}
          {query.isError ? <HistoryError onRetry={() => void query.refetch()} squad={view === 'squad'} /> : null}
          {!query.isLoading && !query.isError && matches.length === 0 ? (
            <HistoryEmpty squad={view === 'squad'} hasSquads={squads.length > 0} />
          ) : null}
          {matches.length > 0 ? <GroupedMatchList matches={matches} onOpen={openMatch} /> : null}

          {query.hasNextPage ? (
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                className="inline-flex h-10 items-center gap-2 rounded-md border border-border px-4 text-sm font-semibold text-text hover:bg-surface disabled:opacity-60"
              >
                {query.isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
                Load older games
              </button>
            </div>
          ) : null}
        </section>
      )}
    </AppPageFrame>
  );
}

function GroupedMatchList({ matches, onOpen }: { matches: MatchHistoryItem[]; onOpen: (match: MatchHistoryItem) => void }) {
  const groups = useMemo(() => {
    const result = new Map<string, MatchHistoryItem[]>();
    matches.forEach((match) => {
      const month = new Intl.DateTimeFormat(undefined, {
        month: 'long', year: 'numeric', timeZone: match.timezone || 'UTC',
      }).format(new Date(match.playedAt));
      result.set(month, [...(result.get(month) ?? []), match]);
    });
    return [...result.entries()];
  }, [matches]);

  return <div className="space-y-7">{groups.map(([month, rows]) => (
    <section key={month}>
      <div className="mb-2 flex items-center gap-3">
        <h2 className="shrink-0 text-sm font-semibold text-text">{month}</h2>
        <div className="h-px flex-1 bg-border" />
      </div>
      <div className="divide-y divide-border border-y border-border">
        {rows.map((match) => <MatchRow key={match.matchId} match={match} onOpen={() => onOpen(match)} />)}
      </div>
    </section>
  ))}</div>;
}

function MatchRow({ match, onOpen }: { match: MatchHistoryItem; onOpen: () => void }) {
  const result = match.viewerResult ?? 'TIE';
  const resultTone = result === 'WIN'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
    : result === 'LOSS'
      ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300'
      : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300';
  const date = new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', timeZone: match.timezone || 'UTC',
  }).format(new Date(match.playedAt));
  const opponents = match.opponentSide.length ? match.opponentSide.join(' & ') : 'Opponent unavailable';
  const title = `${result.charAt(0)} · ${titleCase(match.discipline)} · ${match.title}`;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="grid w-full grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3 py-3 text-left hover:bg-surface/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      aria-label={`Open ${match.title}`}
    >
      <span className={`flex h-11 w-11 items-center justify-center rounded-full text-base font-bold ${resultTone}`}>
        {result.charAt(0)}
      </span>
      <span className="min-w-0">
        <span className="flex min-w-0 items-center gap-2">
          <CircleDot className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span className="truncate text-sm font-semibold text-text" title={title}>{title}</span>
        </span>
        <span className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-muted">
          <span className="truncate">vs {opponents}</span>
          {match.score ? <span className="font-semibold text-text">{match.score}</span> : null}
          <span>{date}</span>
          <span className="truncate">{match.squadName}</span>
          {match.source === 'HISTORICAL_IMPORT' ? <span className="text-text-subtle">Imported</span> : null}
        </span>
      </span>
      <ChevronRight className="h-5 w-5 text-text-subtle" aria-hidden="true" />
    </button>
  );
}

function MatchListSkeleton() {
  return <div className="divide-y divide-border border-y border-border" aria-label="Loading matches">
    {[0, 1, 2].map((item) => <div key={item} className="flex animate-pulse items-center gap-3 py-3">
      <div className="h-11 w-11 rounded-full bg-border" />
      <div className="flex-1 space-y-2"><div className="h-4 w-2/3 rounded bg-border" /><div className="h-3 w-1/2 rounded bg-border" /></div>
    </div>)}
  </div>;
}

function HistoryError({ onRetry, squad }: { onRetry: () => void; squad: boolean }) {
  return <div className="border-y border-border py-10 text-center">
    <p className="font-semibold text-text">{squad ? 'Squad results are unavailable' : 'Your games could not be loaded'}</p>
    <p className="mt-1 text-sm text-text-muted">Check your connection and try again.</p>
    <button type="button" onClick={onRetry} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary">
      <RefreshCw className="h-4 w-4" /> Try again
    </button>
  </div>;
}

function HistoryEmpty({ squad, hasSquads }: { squad: boolean; hasSquads: boolean }) {
  const title = squad && !hasSquads ? 'Join a squad to browse results' : squad ? 'No squad results yet' : 'Your games will appear here';
  const body = squad && !hasSquads
    ? 'Once you join a squad, its shared match history will be available here.'
    : squad ? 'Recorded and imported results will appear after the squad has games.'
      : 'Matches you play in TARK and verified imported results will share this timeline.';
  return <div className="border-y border-border py-12 text-center">
    <Trophy className="mx-auto h-6 w-6 text-text-subtle" />
    <p className="mt-3 font-semibold text-text">{title}</p>
    <p className="mx-auto mt-1 max-w-md text-sm text-text-muted">{body}</p>
  </div>;
}

function titleCase(value: string) {
  const lower = (value || 'match').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
